import {
	carrierName,
	companyContactId,
	currencySchema,
	naicsCodeSchema,
	nonNegativeCurrencySchema,
	suretyAccountId,
	suretyAccountStatusSchema,
	suretyAccountUnderwritingId,
	suretyTypeSchema,
} from "@inrev/common";
import * as z from "zod";
import { defineTool } from "../tool-definition";

const personSchema = z.strictObject({
	name: z.string().nullable(),
	email: z.string(),
	isAgent: z.boolean(),
});

export const getAccount = defineTool({
	name: "get_account",
	description:
		"The account's header: status, agency, primary company, underwriter, agent, followers, tags, capacity and contract history. `bondingLine` is the program bonding line, InRev's total single and aggregate tolerance for the account; quote it to underwriters whether or not `isPublished` is true, since unpublished only means agencies cannot see it. `carrierBondingLines` are the per-carrier lines bonds are placed on, and each can be tighter than the program line; an inactive line is not in use. A line's `underwritingId` is the run that last set it; when it differs from `get_underwriting`'s `underwritingId`, the line predates the latest run and is not that run's output. `naicsCodes` is the account specialty from the NAICS junction. `contractHistory` is what the agency answered about past work. Exposure and action items are not here.",
	label: "Account",
	contexts: ["account", "bond_request", "bond"],
	implementation: "api",
	audience: "model",
	input: z.strictObject({}),
	data: z.strictObject({
		accountId: suretyAccountId,
		status: suretyAccountStatusSchema,
		displayName: z.string().nullable(),
		agencyName: z.string(),
		primaryCompany: z.strictObject({ partyId: companyContactId, name: z.string() }).nullable(),
		underwriter: personSchema,
		agent: personSchema.nullable(),
		followers: z.array(personSchema),
		tags: z.array(z.string()),
		bondingLine: z
			.strictObject({
				single: nonNegativeCurrencySchema,
				aggregate: nonNegativeCurrencySchema,
				rate: z.number(),
				isPublished: z.boolean(),
			})
			.nullable(),
		carrierBondingLines: z.array(
			z.strictObject({
				carrierName,
				suretyType: suretyTypeSchema,
				single: currencySchema,
				aggregate: currencySchema,
				rate: z.number(),
				priority: z.number().int(),
				active: z.boolean(),
				override: z.boolean(),
				overrideNote: z.string().nullable(),
				underwritingId: suretyAccountUnderwritingId.nullable(),
			}),
		),
		naicsCodes: z.array(naicsCodeSchema),
		contractHistory: z.strictObject({
			causedSuretyLoss: z.boolean().nullable(),
			largestProjectCompleted: nonNegativeCurrencySchema.nullable(),
			lostAPaymentSuit: z.boolean().nullable(),
			failedToCompleteAProject: z.boolean().nullable(),
			constructionProjectsGrossLossGt10PercentLast36Months: z.boolean().nullable(),
			currentConstructionProjectExpectedGrossLoss: z.boolean().nullable(),
		}),
	}),
});
